export type AspectRatioOption = {
    value: string;
    label: string;
    tags?: string[];
    isCommon?: boolean;
  };
  
export const ASPECT_RATIO_OPTIONS: AspectRatioOption[] = [
    { value: "1:1", label: "1:1", tags: ["Instagram"], isCommon: true },
    { value: "9:16", label: "9:16", tags: ["TikTok", "Instagram Stories"], isCommon: true },
    { value: "16:9", label: "16:9", tags: ["YouTube"], isCommon: true },
    { value: "4:5", label: "4:5", tags: ["Instagram"], isCommon: true },
    { value: "2:3", label: "2:3", isCommon: false },
    { value: "3:2", label: "3:2", isCommon: false },
    { value: "3:4", label: "3:4", isCommon: false },
    { value: "4:3", label: "4:3", isCommon: false },
    { value: "5:4", label: "5:4", isCommon: false },
    { value: "21:9", label: "21:9", isCommon: false },
];

export type ImageEditorPrompt = {
    id: string;
    name: string;
    description: string;
    prompt: string;
    category: string;
    group: string;
    placeholders?: Record<string, string[]>; // Map of placeholder name to options
  };

export const getImageEditorPrompts = (t: (key: string) => string | undefined): ImageEditorPrompt[] => [
    // === MODEL / APPEARANCE ===
    // Hair Color
    {
      id: "75",
      name: t('social_content.template.75.name') || 'Blonde',
      description: t('social_content.template.75.description') || 'Natural blonde hair color; preserve style and length.',
      prompt:
        "Change the hair to a natural blonde color. Keep the existing hairstyle, length, and texture unchanged. Preserve skin tone, facial features, and clothing. Ensure the new color affects only the hair without spilling onto skin or veil.",
      category: "Hair Color",
      group: "Model",
    },
    {
      id: "76",
      name: t('social_content.template.76.name') || 'Brunette',
      description: t('social_content.template.76.description') || 'Natural brunette hair color; preserve style and length.',
      prompt:
        "Change the hair to a natural brunette color. Keep the hairstyle, length, and texture exactly the same. Maintain skin tone, facial features, and clothing. Ensure the color change stays within the hair and does not bleed onto skin or veil.",
      category: "Hair Color",
      group: "Model",
    },
    {
      id: "77",
      name: t('social_content.template.77.name') || 'Auburn',
      description: t('social_content.template.77.description') || 'Natural auburn hair color; preserve style and length.',
      prompt:
        "Change the hair to a natural auburn color. Preserve the current hairstyle, length, and texture. Keep skin tone, facial features, and outfit unchanged. Apply the color only to the hair, avoiding spill onto skin or veil.",
      category: "Hair Color",
      group: "Model",
    },
    {
      id: "78",
      name: t('social_content.template.78.name') || 'Black',
      description: t('social_content.template.78.description') || 'Natural black hair color; preserve style and length.',
      prompt:
        "Change the hair to a natural black color. Keep the existing hairstyle, length, and texture intact. Maintain skin tone, facial features, and clothing. Make sure the color change is confined to the hair and does not affect skin or veil.",
      category: "Hair Color",
      group: "Model",
    },
    {
      id: "79",
      name: t('social_content.template.79.name') || 'Platinum',
      description: t('social_content.template.79.description') || 'Natural platinum hair color; preserve style and length.',
      prompt:
        "Change the hair to a natural platinum color. Preserve the hairstyle, length, and texture. Maintain skin tone, facial features, and outfit. Apply the color only within the hair area without spilling onto skin or veil.",
      category: "Hair Color",
      group: "Model",
    },
    {
      id: "80",
      name: t('social_content.template.80.name') || 'Balayage',
      description: t('social_content.template.80.description') || 'Natural gradient highlights; preserve style and length.',
      prompt:
        "Apply a balayage effect to the hair with harmonious transitions between base and highlights. Retain the existing haircut, length, and style. Maintain natural strand texture and sheen. Keep skin tone, facial features, and outfit unchanged.",
      category: "Hair Color",
      group: "Model",
    },
  
    // Hair Length & Texture
    // Hair Length
    {
      id: "91",
      name: t('social_content.template.91.name') || 'Pixie',
      description: t('social_content.template.91.description') || 'Pixie cut length; preserve color and texture.',
      prompt:
        "Set the hair length to a pixie cut. Keep the hair’s natural volume, color, and texture intact. Maintain clean edges around the veil and shoulders, and preserve face shape, accessories, and other features.",
      category: "Hair Length & Texture",
      group: "Model",
    },
    {
      id: "92",
      name: t('social_content.template.92.name') || 'Bob',
      description: t('social_content.template.92.description') || 'Bob cut length; preserve color and texture.',
      prompt:
        "Set the hair length to a bob cut. Retain the hair’s natural volume, color, and texture. Maintain neat edges around the veil and shoulders. Keep face shape, accessories, and other features unchanged.",
      category: "Hair Length & Texture",
      group: "Model",
    },
    {
      id: "93",
      name: t('social_content.template.93.name') || 'Shoulder',
      description: t('social_content.template.93.description') || 'Shoulder length; preserve color and texture.',
      prompt:
        "Set the hair length to shoulder length. Preserve the existing hair color, texture, and natural volume. Ensure clean edges near the veil and shoulders. Keep face shape, accessories, and other features the same.",
      category: "Hair Length & Texture",
      group: "Model",
    },
    {
      id: "94",
      name: t('social_content.template.94.name') || 'Long',
      description: t('social_content.template.94.description') || 'Long hair length; preserve color and texture.',
      prompt:
        "Extend the hair to a long length. Maintain the current color, texture, and natural volume. Preserve clean edges around the veil and shoulders. Keep facial features, accessories, and other aspects unchanged.",
      category: "Hair Length & Texture",
      group: "Model",
    },
    {
      id: "95",
      name: t('social_content.template.95.name') || 'Extra Long',
      description: t('social_content.template.95.description') || 'Extra long hair length; preserve color and texture.',
      prompt:
        "Extend the hair to an extra‑long length. Retain the existing hair color, texture, and volume. Keep edges tidy around the veil and shoulders. Preserve face shape, accessories, and all other features.",
      category: "Hair Length & Texture",
      group: "Model",
    },
    // Hair Texture
    {
      id: "96",
      name: t('social_content.template.96.name') || 'Straight',
      description: t('social_content.template.96.description') || 'Straight hair texture; preserve length and color.',
      prompt:
        "Set the hair texture to straight. Maintain the current hair length, color, and natural volume. Keep edges neat around the veil and shoulders. Preserve face shape, accessories, and other aspects.",
      category: "Hair Length & Texture",
      group: "Model",
    },
    {
      id: "97",
      name: t('social_content.template.97.name') || 'Wavy',
      description: t('social_content.template.97.description') || 'Wavy hair texture; preserve length and color.',
      prompt:
        "Set the hair texture to wavy. Keep the existing hair length, color, and volume. Maintain clean edges near the veil and shoulders. Preserve face shape, accessories, and all other features.",
      category: "Hair Length & Texture",
      group: "Model",
    },
    {
      id: "98",
      name: t('social_content.template.98.name') || 'Curly',
      description: t('social_content.template.98.description') || 'Curly hair texture; preserve length and color.',
      prompt:
        "Set the hair texture to curly. Retain the current hair length, color, and natural volume. Keep edges tidy around the veil and shoulders. Preserve face shape, accessories, and other aspects.",
      category: "Hair Length & Texture",
      group: "Model",
    },
    {
      id: "99",
      name: t('social_content.template.99.name') || 'Coiled',
      description: t('social_content.template.99.description') || 'Coiled hair texture; preserve length and color.',
      prompt:
        "Set the hair texture to coiled. Maintain the existing hair length, color, and volume. Ensure clean edges near the veil and shoulders. Keep face shape, accessories, and other features unchanged.",
      category: "Hair Length & Texture",
      group: "Model",
    },
  
    // Hair Style
    {
      id: "100",
      name: t('social_content.template.100.name') || 'Updo',
      description: t('social_content.template.100.description') || 'Updo hairstyle; preserve color and length.',
      prompt:
        "Style the hair into an updo. Keep the current hair color and length intact. Maintain realistic strand detail and natural gravity, ensuring the hair does not intersect the dress or face. Preserve makeup, facial expression, and outfit.",
      category: "Hair Style",
      group: "Model",
    },
    {
      id: "101",
      name: t('social_content.template.101.name') || 'Loose Waves',
      description: t('social_content.template.101.description') || 'Loose waves hairstyle; preserve color and length.',
      prompt:
        "Style the hair into loose waves. Maintain the existing hair color and length. Keep realistic strand detail and natural movement, avoiding any overlap with the dress or face. Preserve makeup, facial expression, and outfit.",
      category: "Hair Style",
      group: "Model",
    },
    {
      id: "102",
      name: t('social_content.template.102.name') || 'Ponytail',
      description: t('social_content.template.102.description') || 'Ponytail hairstyle; preserve color and length.',
      prompt:
        "Style the hair into a ponytail. Keep the hair’s color and length unchanged. Maintain realistic strand detail and natural gravity, ensuring no clipping into the dress or face. Preserve makeup, facial expression, and outfit.",
      category: "Hair Style",
      group: "Model",
    },
    {
      id: "103",
      name: t('social_content.template.103.name') || 'Half-Up',
      description: t('social_content.template.103.description') || 'Half-up hairstyle; preserve color and length.',
      prompt:
        "Style the hair into a half‑up arrangement. Retain the current hair color and length. Maintain realistic strand detail and natural movement, avoiding overlap with the dress or face. Preserve makeup, facial expression, and outfit.",
      category: "Hair Style",
      group: "Model",
    },
    {
      id: "104",
      name: t('social_content.template.104.name') || 'Braided',
      description: t('social_content.template.104.description') || 'Braided hairstyle; preserve color and length.',
      prompt:
        "Style the hair into braids. Keep the existing hair color and length. Maintain realistic strand detail and natural gravity, ensuring no intersection with the dress or face. Preserve makeup, facial expression, and outfit.",
      category: "Hair Style",
      group: "Model",
    },
    {
      id: "105",
      name: t('social_content.template.105.name') || 'Windblown',
      description: t('social_content.template.105.description') || 'Windblown hairstyle; preserve color and length.',
      prompt:
        "Style the hair to look windblown. Keep the current hair color and length. Maintain realistic strand detail and natural gravity, ensuring hair does not cover the face or dress unnaturally. Preserve makeup, facial expression, and outfit.",
      category: "Hair Style",
      group: "Model",
    },
    {
      id: "106",
      name: t('social_content.template.106.name') || 'Veil Integrated',
      description: t('social_content.template.106.description') || 'Veil integrated hairstyle; preserve color and length.',
      prompt:
        "Style the hair to integrate smoothly with the veil. Keep the existing hair color and length. Maintain realistic strand detail and natural gravity, ensuring hair and veil blend without clipping into the dress or face. Preserve makeup, facial expression, and outfit.",
      category: "Hair Style",
      group: "Model",
    },
  
    // Makeup
    {
      id: "81",
      name: t('social_content.template.81.name') || 'Nude',
      description: t('social_content.template.81.description') || 'Natural nude lip color; clean edges; natural finish.',
      prompt:
        "Change the lip color to a natural nude shade while keeping the existing finish (matte or satin). Preserve the lip texture and clean edges, ensuring no color bleeds onto teeth or skin. Keep all other facial features and makeup unchanged.",
      category: "Makeup",
      group: "Model",
    },
    {
      id: "82",
      name: t('social_content.template.82.name') || 'Soft Pink',
      description: t('social_content.template.82.description') || 'Soft pink lip color; clean edges; natural finish.',
      prompt:
        "Change the lip color to a soft pink shade while keeping the current finish consistent. Maintain the lip’s natural texture and crisp edges, avoiding any bleeding onto teeth or skin. Preserve all other facial features and makeup.",
      category: "Makeup",
      group: "Model",
    },
    {
      id: "83",
      name: t('social_content.template.83.name') || 'Bold Red',
      description: t('social_content.template.83.description') || 'Bold red lip color; clean edges; natural finish.',
      prompt:
        "Change the lip color to a bold red shade while retaining the existing finish. Keep the lip texture and clean edges intact, ensuring no color spreads onto teeth or skin. Maintain all other facial features and makeup.",
      category: "Makeup",
      group: "Model",
    },
    {
      id: "84",
      name: t('social_content.template.84.name') || 'Coral',
      description: t('social_content.template.84.description') || 'Coral lip color; clean edges; natural finish.',
      prompt:
        "Change the lip color to a coral shade while keeping the current finish. Preserve the lip texture and well‑defined edges, preventing color from bleeding onto teeth or skin. Keep all other facial features and makeup unchanged.",
      category: "Makeup",
      group: "Model",
    },
  
    // Eye Color
    {
      id: "85",
      name: t('social_content.template.85.name') || 'Blue',
      description: t('social_content.template.85.description') || 'Change iris color to blue; preserve detail and catchlights.',
      prompt:
        "Change the iris color to blue. Preserve the iris structure, catchlights, and natural sclera tone. Keep all other facial features, makeup, and expression exactly the same.",
      category: "Eye Color",
      group: "Model",
    },
    {
      id: "86",
      name: t('social_content.template.86.name') || 'Green',
      description: t('social_content.template.86.description') || 'Change iris color to green; preserve detail and catchlights.',
      prompt:
        "Change the iris color to green. Retain the iris detail, catchlights, and natural sclera tint. Preserve all other facial features, makeup, and expression.",
      category: "Eye Color",
      group: "Model",
    },
    {
      id: "87",
      name: t('social_content.template.87.name') || 'Hazel',
      description: t('social_content.template.87.description') || 'Change iris color to hazel; preserve detail and catchlights.',
      prompt:
        "Change the iris color to hazel. Keep the iris detail, catchlights, and sclera tone intact. Maintain all other facial features, makeup, and expression.",
      category: "Eye Color",
      group: "Model",
    },
    {
      id: "88",
      name: t('social_content.template.88.name') || 'Brown',
      description: t('social_content.template.88.description') || 'Change iris color to brown; preserve detail and catchlights.',
      prompt:
        "Change the iris color to brown. Preserve the iris structure, catchlights, and natural sclera hue. Keep all other facial features, makeup, and expression unchanged.",
      category: "Eye Color",
      group: "Model",
    },
    {
      id: "89",
      name: t('social_content.template.89.name') || 'Grey',
      description: t('social_content.template.89.description') || 'Change iris color to grey; preserve detail and catchlights.',
      prompt:
        "Change the iris color to grey. Maintain the iris detail, catchlights, and sclera tone. Preserve all other facial features, makeup, and expression.",
      category: "Eye Color",
      group: "Model",
    },
    {
      id: "90",
      name: t('social_content.template.90.name') || 'Black',
      description: t('social_content.template.90.description') || 'Change iris color to black; preserve detail and catchlights.',
      prompt:
        "Change the iris color to black. Retain the iris detail, catchlights, and sclera tint. Keep all other facial features, makeup, and expression unchanged.",
      category: "Eye Color",
      group: "Model",
    },
  
    // === EXPRESSIONS / POSES (MODEL) ===
    // Expressions
    {
      id: "124",
      name: t('social_content.template.124.name') || 'Neutral',
      description: t('social_content.template.124.description') || 'Neutral facial expression; natural and composed.',
      prompt:
        "Set the facial expression to neutral. Preserve all other facial features, makeup, pose, and lighting. Ensure the expression looks natural, composed, and photorealistic.",
      category: "Expressions",
      group: "Model",
    },
    {
      id: "125",
      name: t('social_content.template.125.name') || 'Soft Smile',
      description: t('social_content.template.125.description') || 'Gentle, subtle smile; warm and approachable.',
      prompt:
        "Set the facial expression to a soft smile. Keep all other facial features, makeup, pose, and lighting intact. The smile should appear gentle, warm, approachable, and photorealistic.",
      category: "Expressions",
      group: "Model",
    },
    {
      id: "126",
      name: t('social_content.template.126.name') || 'Broad Smile',
      description: t('social_content.template.126.description') || 'Wide, joyful smile; energetic and happy.',
      prompt:
        "Set the facial expression to a broad smile. Preserve all other facial features, makeup, pose, and lighting. The expression should look joyful, energetic, and photorealistic.",
      category: "Expressions",
      group: "Model",
    },
    {
      id: "127",
      name: t('social_content.template.127.name') || 'Laugh',
      description: t('social_content.template.127.description') || 'Genuine laugh expression; authentic and joyful.',
      prompt:
        "Set the facial expression to a genuine laugh. Maintain all other facial features, makeup, pose, and lighting. The laugh should appear authentic, joyful, and photorealistic.",
      category: "Expressions",
      group: "Model",
    },
    {
      id: "128",
      name: t('social_content.template.128.name') || 'Pensive',
      description: t('social_content.template.128.description') || 'Thoughtful, contemplative expression; introspective.',
      prompt:
        "Set the facial expression to pensive. Keep all other facial features, makeup, pose, and lighting unchanged. The expression should appear natural, thoughtful, contemplative, and photorealistic.",
      category: "Expressions",
      group: "Model",
    },
    {
      id: "129",
      name: t('social_content.template.129.name') || 'Focused',
      description: t('social_content.template.129.description') || 'Concentrated, determined expression; intense gaze.',
      prompt:
        "Set the facial expression to focused. Preserve all other facial features, makeup, pose, and lighting. The expression should appear natural, concentrated, determined, and photorealistic.",
      category: "Expressions",
      group: "Model",
    },
    {
      id: "130",
      name: t('social_content.template.130.name') || 'Romantic Gaze',
      description: t('social_content.template.130.description') || 'Romantic, dreamy expression; soft and loving.',
      prompt:
        "Set the facial expression to a romantic gaze. Keep all other facial features, makeup, pose, and lighting unchanged. The gaze should appear dreamy, soft, loving, and photorealistic.",
      category: "Expressions",
      group: "Model",
    },
  
    // === POSTURE & MOTION ===
    // Posture
    {
      id: "131",
      name: t('social_content.template.131.name') || 'Relaxed',
      description: t('social_content.template.131.description') || 'Relaxed body posture; natural and comfortable stance.',
      prompt:
        "Set the model’s body posture to relaxed. Preserve the current expression, outfit, and other pose details. Ensure the body positioning looks natural and photorealistic.",
      category: "Posture",
      group: "Posture & Motion",
    },
    {
      id: "132",
      name: t('social_content.template.132.name') || 'Upright',
      description: t('social_content.template.132.description') || 'Upright body posture; straight and confident stance.',
      prompt:
        "Set the model’s body posture to upright. Keep the existing expression, outfit, and other pose elements the same. Ensure the posture appears natural, straight, confident, and photorealistic.",
      category: "Posture",
      group: "Posture & Motion",
    },
    {
      id: "133",
      name: t('social_content.template.133.name') || 'Leaning',
      description: t('social_content.template.133.description') || 'Leaning body posture; dynamic and casual stance.',
      prompt:
        "Set the model’s body posture to leaning. Preserve the current expression, outfit, and other pose details. The leaning stance should look natural, dynamic, casual, and photorealistic.",
      category: "Posture",
      group: "Posture & Motion",
    },
    {
      id: "134",
      name: t('social_content.template.134.name') || 'Dynamic',
      description: t('social_content.template.134.description') || 'Dynamic body posture; energetic and active stance.',
      prompt:
        "Set the model’s body posture to dynamic. Keep the existing expression, outfit, and other pose elements. Ensure the posture appears energetic, active, natural, and photorealistic.",
      category: "Posture",
      group: "Posture & Motion",
    },
    {
      id: "135",
      name: t('social_content.template.135.name') || 'Twisting',
      description: t('social_content.template.135.description') || 'Twisting body posture; elegant and fluid stance.',
      prompt:
        "Set the model’s body posture to twisting. Preserve the current expression, outfit, and other pose details. The twisting pose should appear elegant, fluid, natural, and photorealistic.",
      category: "Posture",
      group: "Posture & Motion",
    },
    {
      id: "136",
      name: t('social_content.template.136.name') || 'Walking',
      description: t('social_content.template.136.description') || 'Walking body posture; natural movement stance.',
      prompt:
        "Set the model’s body posture to walking. Maintain the existing expression, outfit, and other pose elements. The walking stance should look natural, fluid, and photorealistic.",
      category: "Posture",
      group: "Posture & Motion",
    },
    // Body Direction
    {
      id: "137",
      name: t('social_content.template.137.name') || 'Facing Camera',
      description: t('social_content.template.137.description') || 'Body facing directly toward the camera.',
      prompt:
        "Rotate the body to face the camera directly. Preserve facial expression, outfit, and all other pose elements. Ensure the orientation looks natural and photorealistic.",
      category: "Body Direction",
      group: "Posture & Motion",
    },
    {
      id: "138",
      name: t('social_content.template.138.name') || '¾ Turn',
      description: t('social_content.template.138.description') || 'Body at three-quarter turn angle.',
      prompt:
        "Rotate the body to a three‑quarter turn angle. Keep the facial expression, outfit, and other pose elements intact. Ensure the positioning appears natural and photorealistic.",
      category: "Body Direction",
      group: "Posture & Motion",
    },
    {
      id: "139",
      name: t('social_content.template.139.name') || 'Profile',
      description: t('social_content.template.139.description') || 'Body in side profile view.',
      prompt:
        "Rotate the body to a side profile view. Preserve facial expression, outfit, and all other pose elements. Ensure the positioning looks natural and photorealistic.",
      category: "Body Direction",
      group: "Posture & Motion",
    },
    {
      id: "140",
      name: t('social_content.template.140.name') || 'Back Turn',
      description: t('social_content.template.140.description') || 'Body turned away from camera.',
      prompt:
        "Turn the body so the back faces the camera. Maintain facial expression, outfit, and other pose elements. Ensure the orientation looks natural and photorealistic.",
      category: "Body Direction",
      group: "Posture & Motion",
    },
    // Motion
    {
      id: "141",
      name: t('social_content.template.141.name') || 'Still',
      description: t('social_content.template.141.description') || 'No motion effect; static pose.',
      prompt:
        "Apply a still effect to the image so there is no motion blur. Preserve facial expression, outfit, and all other pose elements. Ensure the scene remains natural and photorealistic.",
      category: "Motion",
      group: "Posture & Motion",
    },
    {
      id: "142",
      name: t('social_content.template.142.name') || 'Wind Effect',
      description: t('social_content.template.142.description') || 'Wind effect; fabric and hair movement.',
      prompt:
        "Add a gentle wind effect to the scene, creating natural movement in the hair and fabric. Maintain the subject’s expression, outfit, and pose. Ensure the motion appears realistic and photorealistic while preserving overall composition.",
      category: "Motion",
      group: "Posture & Motion",
    },
    {
      id: "143",
      name: t('social_content.template.143.name') || 'Twirl',
      description: t('social_content.template.143.description') || 'Twirling motion; dynamic spinning movement.',
      prompt:
        "Add a twirling motion effect to the subject, suggesting a dynamic spinning movement. Preserve the facial expression, outfit, and other pose elements. Ensure the motion appears natural, dynamic, and photorealistic.",
      category: "Motion",
      group: "Posture & Motion",
    },
    {
      id: "144",
      name: t('social_content.template.144.name') || 'Step Forward',
      description: t('social_content.template.144.description') || 'Stepping forward motion; natural walking movement.',
      prompt:
        "Add a step‑forward motion to the subject, conveying a natural walking movement. Keep facial expression, outfit, and other pose elements unchanged. Ensure the motion appears natural, fluid, and photorealistic.",
      category: "Motion",
      group: "Posture & Motion",
    },
  
    // === POSE DETAILS ===
    // Head Tilt
    {
      id: "145",
      name: t('social_content.template.145.name') || 'Neutral',
      description: t('social_content.template.145.description') || 'Neutral head tilt; natural and balanced.',
      prompt:
        "Set the head tilt to neutral. Keep facial expression, makeup, and other pose elements the same. Ensure the head position looks natural, balanced, and photorealistic.",
      category: "Head Tilt",
      group: "Model",
    },
    {
      id: "146",
      name: t('social_content.template.146.name') || 'Down',
      description: t('social_content.template.146.description') || 'Head tilted down; subtle and elegant.',
      prompt:
        "Tilt the head downward. Preserve facial expression, makeup, and all other pose elements. Ensure the head position looks subtle, elegant, natural, and photorealistic.",
      category: "Head Tilt",
      group: "Model",
    },
    {
      id: "147",
      name: t('social_content.template.147.name') || 'Up',
      description: t('social_content.template.147.description') || 'Head tilted up; confident and uplifting.',
      prompt:
        "Tilt the head upward. Maintain facial expression, makeup, and all other pose elements. Ensure the head position appears confident, uplifting, natural, and photorealistic.",
      category: "Head Tilt",
      group: "Model",
    },
    {
      id: "148",
      name: t('social_content.template.148.name') || 'Side',
      description: t('social_content.template.148.description') || 'Head tilted to the side; playful and dynamic.',
      prompt:
        "Tilt the head to the side. Preserve facial expression, makeup, and other pose elements. Ensure the head position appears playful, dynamic, natural, and photorealistic.",
      category: "Head Tilt",
      group: "Model",
    },
    // Arms & Hands
    {
      id: "149",
      name: t('social_content.template.149.name') || 'Arms Down',
      description: t('social_content.template.149.description') || 'Arms and hands positioned down; natural and relaxed.',
      prompt:
        "Set the arms and hands to rest down by the sides. Preserve facial expression, outfit, and all other pose elements. Ensure the arm positioning looks natural, relaxed, and photorealistic.",
      category: "Arms & Hands",
      group: "Model",
    },
    {
      id: "150",
      name: t('social_content.template.150.name') || 'Crossed',
      description: t('social_content.template.150.description') || 'Arms crossed; confident and composed.',
      prompt:
        "Set the arms and hands to a crossed position across the torso. Keep facial expression, outfit, and other pose elements unchanged. Ensure the arm positioning looks confident, composed, natural, and photorealistic.",
      category: "Arms & Hands",
      group: "Model",
    },
    {
      id: "151",
      name: t('social_content.template.151.name') || 'Reaching Out',
      description: t('social_content.template.151.description') || 'Arms reaching out; open and inviting.',
      prompt:
        "Position the arms and hands reaching outward in an open gesture. Maintain the current facial expression, outfit, and other pose elements. Ensure the arm positioning appears inviting, natural, and photorealistic.",
      category: "Arms & Hands",
      group: "Model",
    },
    {
      id: "152",
      name: t('social_content.template.152.name') || 'Playful Lift',
      description: t('social_content.template.152.description') || 'Arms playfully lifted; dynamic and energetic.',
      prompt:
        "Lift the arms and hands playfully. Keep facial expression, outfit, and other pose details unchanged. Ensure the arm positioning looks dynamic, energetic, natural, and photorealistic.",
      category: "Arms & Hands",
      group: "Model",
    },
    // Legs
    {
      id: "153",
      name: t('social_content.template.153.name') || 'Standing',
      description: t('social_content.template.153.description') || 'Standing leg position; stable and grounded.',
      prompt:
        "Set the legs to a stable standing position. Preserve facial expression, outfit, and other pose elements. Ensure the leg positioning looks natural, grounded, and photorealistic.",
      category: "Legs",
      group: "Model",
    },
    {
      id: "154",
      name: t('social_content.template.154.name') || 'Walking',
      description: t('social_content.template.154.description') || 'Walking leg position; natural movement stance.',
      prompt:
        "Set the legs to a walking position to suggest natural movement. Keep facial expression, outfit, and other pose elements unchanged. Ensure the leg positioning looks natural, fluid, and photorealistic.",
      category: "Legs",
      group: "Model",
    },
    {
      id: "155",
      name: t('social_content.template.155.name') || 'Slight Bend',
      description: t('social_content.template.155.description') || 'Slight bend in legs; relaxed and natural.',
      prompt:
        "Set the legs to a relaxed stance with a slight bend. Maintain facial expression, outfit, and other pose elements. Ensure the leg positioning looks natural, relaxed, and photorealistic.",
      category: "Legs",
      group: "Model",
    },
    {
      id: "156",
      name: t('social_content.template.156.name') || 'Extended',
      description: t('social_content.template.156.description') || 'Legs extended; elegant and elongated.',
      prompt:
        "Set the legs to an extended position to create an elegant, elongated look. Preserve facial expression, outfit, and other pose elements. Ensure the leg positioning appears natural, elegant, and photorealistic.",
      category: "Legs",
      group: "Model",
    },
    {
      id: "157",
      name: t('social_content.template.157.name') || 'Sitting',
      description: t('social_content.template.157.description') || 'Sitting leg position; composed and relaxed.',
      prompt:
        "Set the legs to a sitting position. Keep facial expression, outfit, and other pose elements unchanged. Ensure the seated posture looks natural, composed, and photorealistic.",
      category: "Legs",
      group: "Model",
    },
  
    // === SCENE / ENVIRONMENT ===
    // Classic & Architectural Environments
    {
      id: "158",
      name: t('social_content.template.158.name') || 'Royal Palace Hall',
      description: t('social_content.template.158.description') || 'Marble floors, chandeliers, high ceilings; elegant and timeless.',
      prompt:
        "Replace the background with a royal palace hall. Include polished marble floors with intricate patterns, grand crystal chandeliers hanging from high vaulted ceilings, and ornate architectural details under soft ambient lighting. Keep the subject’s pose, outfit, and lighting direction unchanged. Ensure natural shadow grounding and avoid adding other people or modern elements.",
      category: "Classic & Architectural",
      group: "Scene",
    },
    {
      id: "159",
      name: t('social_content.template.159.name') || 'Grand Staircase Interior',
      description: t('social_content.template.159.description') || 'Opulent light, depth behind subject; majestic elegance.',
      prompt:
        "Set the background to a grand staircase interior. Feature a sweeping marble or stone staircase with ornate railings and opulent lighting that creates depth behind the subject. Preserve the subject’s scale, pose, outfit, and lighting direction. Ensure natural perspective and shadow grounding without adding crowds or modern elements.",
      category: "Classic & Architectural",
      group: "Scene",
    },
    {
      id: "160",
      name: t('social_content.template.160.name') || 'French Château Ballroom',
      description: t('social_content.template.160.description') || 'Gold trim, tall windows, romantic tones; luxurious atmosphere.',
      prompt:
        "Replace the background with a French château ballroom. Use gilded gold trim and moldings, tall arched windows with soft natural light, warm romantic tones, and parquet floors. Maintain the subject’s lighting direction and color accuracy. Keep the pose and outfit intact and avoid crowds or modern intrusions.",
      category: "Classic & Architectural",
      group: "Scene",
    },
    {
      id: "161",
      name: t('social_content.template.161.name') || 'Venetian Corridor',
      description: t('social_content.template.161.description') || 'Arches, soft daylight, heritage feel; timeless elegance.',
      prompt:
        "Replace the background with a Venetian corridor featuring elegant arches and columns. Use soft daylight filtering through warm stone tones and heritage architectural details. Keep the subject’s scale and lighting consistent. Avoid modern elements or crowds.",
      category: "Classic & Architectural",
      group: "Scene",
    },
    {
      id: "162",
      name: t('social_content.template.162.name') || 'Historic Chapel Interior',
      description: t('social_content.template.162.description') || 'Stained glass glow, ceremonial elegance; sacred atmosphere.',
      prompt:
        "Set the background to a historic chapel interior. Include stained glass windows casting colorful light, ceremonial elegance, and stone or wood architectural details. Maintain the subject’s pose, outfit, and lighting direction. Ensure natural shadow grounding and exclude crowds or modern elements.",
      category: "Classic & Architectural",
      group: "Scene",
    },
    {
      id: "163",
      name: t('social_content.template.163.name') || 'Art Museum Hall',
      description: t('social_content.template.163.description') || 'Neutral tones, minimal art-deco details; refined sophistication.',
      prompt:
        "Replace the background with an art museum hall featuring neutral tones, minimal art‑deco architectural details, clean lines, polished floors, and soft gallery lighting. Maintain the subject’s scale and lighting direction. Preserve the pose and outfit, and avoid visible artwork, crowds, or signage.",
      category: "Classic & Architectural",
      group: "Scene",
    },
    {
      id: "164",
      name: t('social_content.template.164.name') || 'Classic Italian Villa Terrace',
      description: t('social_content.template.164.description') || 'Sunlit stone, climbing vines; Mediterranean charm.',
      prompt:
        "Set the background to a classic Italian villa terrace. Include sunlit stone terraces, climbing vines and greenery, Mediterranean architecture, and warm golden light. Keep the subject’s lighting consistent and preserve pose and outfit. Avoid crowds or modern elements.",
      category: "Classic & Architectural",
      group: "Scene",
    },
    // Natural & Outdoor Environments
    {
      id: "185",
      name: t('social_content.template.185.name') || 'Sahara Desert Background',
      description: t('social_content.template.185.description') || 'Set the scene in the Sahara Desert',
      prompt:
        "Transform the background to the vast Sahara Desert: undulating golden sand dunes with soft shadows and wind-swept textures, crystal clear blue sky with minimal clouds, warm golden hour sunlight creating long, dramatic shadows. Maintain the subject's exact pose, outfit, and lighting direction while ensuring natural shadow grounding on the sand. Create an empty, serene desert landscape with no additional people, structures, or props.",
      category: "Natural & Outdoor",
      group: "Scene"
    },
    {
      id: "165",
      name: t('social_content.template.165.name') || 'Garden Wedding Setting',
      description: t('social_content.template.165.description') || 'Floral arch, greenery, natural daylight; romantic and fresh.',
      prompt:
        "Replace the background with a garden wedding setting. Feature a floral arch or pergola, lush greenery, and natural daylight filtering through. Maintain the subject’s pose, outfit, and lighting. Ensure natural integration with the garden and avoid crowds or signage.",
      category: "Natural & Outdoor",
      group: "Scene",
    },
    {
      id: "166",
      name: t('social_content.template.166.name') || 'Forest Glade',
      description: t('social_content.template.166.description') || 'Filtered sunlight, soft depth, organic backdrop; serene natural beauty.',
      prompt:
        "Set the background to a forest glade with trees creating filtered sunlight and a soft depth of field. Use a gentle green palette and organic natural backdrop. Maintain the subject’s color accuracy and lighting. Avoid wildlife or extra people.",
      category: "Natural & Outdoor",
      group: "Scene",
    },
    {
      id: "167",
      name: t('social_content.template.167.name') || 'Meadow with Wildflowers',
      description: t('social_content.template.167.description') || 'Pastel tones, open space; fresh and airy.',
      prompt:
        "Replace the background with a meadow filled with blooming wildflowers in pastel tones and open space. Use soft natural light and gentle breeze effects for a serene countryside atmosphere. Maintain the subject’s scale, pose, and lighting direction. Avoid crowds or structures.",
      category: "Natural & Outdoor",
      group: "Scene",
    },
    {
      id: "168",
      name: t('social_content.template.168.name') || 'Vineyard Landscape',
      description: t('social_content.template.168.description') || 'Gentle hills, warm tones, rustic charm; pastoral elegance.',
      prompt:
        "Set the scene against a vineyard landscape with rolling gentle hills covered in grapevines, warm golden tones, and rustic charm. Keep the subject’s lighting direction and scale consistent. Preserve pose and outfit, and avoid crowds or modern elements.",
      category: "Natural & Outdoor",
      group: "Scene",
    },
    {
      id: "169",
      name: t('social_content.template.169.name') || 'Mountain Overlook',
      description: t('social_content.template.169.description') || 'Vast background, serene sky; majestic and expansive.',
      prompt:
        "Replace the background with a mountain overlook featuring distant mountain ranges, a serene sky, and a dramatic natural landscape. Keep the subject’s lighting consistent and preserve pose and outfit. Ensure natural shadow grounding and avoid crowds or structures.",
      category: "Natural & Outdoor",
      group: "Scene",
    },
    {
      id: "170",
      name: t('social_content.template.170.name') || 'Lakeside Scene',
      description: t('social_content.template.170.description') || 'Reflections, tranquil horizon; peaceful and serene.',
      prompt:
        "Set the background to a lakeside scene with calm water reflecting the horizon and natural shoreline under soft light. Maintain the subject’s scale and perspective. Preserve pose and outfit, and avoid crowds, boats, or modern elements.",
      category: "Natural & Outdoor",
      group: "Scene",
    },
    {
      id: "171",
      name: t('social_content.template.171.name') || 'Lavender Field',
      description: t('social_content.template.171.description') || 'Purple haze, bright summer mood; vibrant and romantic.',
      prompt:
        "Replace the background with a lavender field featuring rows of blooming lavender receding into the distance, soft purple hues, and a bright summer mood with golden hour warmth. Preserve dress color neutrality and natural skin tones. Maintain the subject’s scale and avoid crowds or structures.",
      category: "Natural & Outdoor",
      group: "Scene",
    },
    // Modern & Urban Environments
    {
      id: "172",
      name: t('social_content.template.172.name') || 'Minimal White Loft',
      description: t('social_content.template.172.description') || 'Clean space, floor-to-ceiling windows; contemporary elegance.',
      prompt:
        "Replace the background with a minimal white loft featuring clean white space, floor‑to‑ceiling windows with natural light, modern architectural lines, and polished floors. Keep the subject’s scale and lighting direction consistent. Preserve pose and outfit, and avoid adding furniture, crowds, or text signage.",
      category: "Modern & Urban",
      group: "Scene",
    },
    {
      id: "173",
      name: t('social_content.template.173.name') || 'City Rooftop Terrace',
      description: t('social_content.template.173.description') || 'Skyline in background, golden hour tone; urban sophistication.',
      prompt:
        "Set the background to a city rooftop terrace with a distant skyline, golden hour lighting, and a sophisticated urban setting. Maintain the subject’s scale and key light direction. Keep pose and outfit intact and avoid crowds, text signage, or advertisements.",
      category: "Modern & Urban",
      group: "Scene",
    },
    {
      id: "174",
      name: t('social_content.template.174.name') || 'Industrial Studio',
      description: t('social_content.template.174.description') || 'Textured walls, moody elegance; contemporary edge.',
      prompt:
        "Replace the background with an industrial studio featuring textured brick or concrete walls, moody elegant lighting, and exposed architectural elements for a contemporary edge. Maintain the subject’s lighting direction and color accuracy. Preserve pose and outfit, and avoid crowds or clutter.",
      category: "Modern & Urban",
      group: "Scene",
    },
    {
      id: "175",
      name: t('social_content.template.175.name') || 'Modern Glass Atrium',
      description: t('social_content.template.175.description') || 'Bright, geometric lines, reflective light; sleek modernism.',
      prompt:
        "Set the scene in a modern glass atrium with bright natural light, geometric architectural lines, and reflective surfaces for a sleek modern design. Keep the subject’s lighting consistent and preserve pose and outfit. Ensure natural shadow grounding and avoid crowds, furniture, or text.",
      category: "Modern & Urban",
      group: "Scene",
    },
    {
      id: "176",
      name: t('social_content.template.176.name') || 'Urban Street Corner',
      description: t('social_content.template.176.description') || 'Chic editorial wedding vibe; sophisticated city setting.',
      prompt:
        "Replace the background with an urban street corner that conveys a chic editorial wedding vibe and sophisticated city setting with tasteful architecture and soft bokeh. Maintain the subject’s scale and perspective. Preserve pose and outfit, and avoid legible text, license plates, crowds, or modern signage.",
      category: "Modern & Urban",
      group: "Scene",
    },
    {
      id: "177",
      name: t('social_content.template.177.name') || 'Contemporary Art Gallery',
      description: t('social_content.template.177.description') || 'White walls, polished floor, refined composition; minimalist sophistication.',
      prompt:
        "Replace the background with a contemporary art gallery featuring white walls, a polished floor, refined composition, and minimalist sophistication with soft gallery lighting. Maintain the subject’s scale and lighting direction. Preserve pose and outfit, and avoid visible artwork, crowds, or signage.",
      category: "Modern & Urban",
      group: "Scene",
    },
    // Dreamlike & Seasonal Environments
    {
      id: "178",
      name: t('social_content.template.178.name') || 'Winter Wonderland Courtyard',
      description: t('social_content.template.178.description') || 'Frosted trees, cool light; magical winter atmosphere.',
      prompt:
        "Set the scene as a winter wonderland courtyard with frosted trees and branches, cool soft light, and gently falling snow to create a magical winter atmosphere. Keep the subject and outfit unchanged. Apply snow only to background surfaces, not on the subject’s skin or clothing, and maintain the original lighting direction.",
      category: "Dreamlike & Seasonal",
      group: "Scene",
    },
    {
      id: "179",
      name: t('social_content.template.179.name') || 'Autumn Forest Path',
      description: t('social_content.template.179.description') || 'Golden leaves, depth perspective; warm seasonal beauty.',
      prompt:
        "Replace the background with an autumn forest path featuring golden and amber leaves, depth perspective with trees receding, and warm seasonal tones under soft natural light. Preserve the subject’s color accuracy and lighting. Maintain pose and outfit, and avoid crowds or wildlife.",
      category: "Dreamlike & Seasonal",
      group: "Scene",
    },
    {
      id: "180",
      name: t('social_content.template.180.name') || 'Spring Blossom Garden',
      description: t('social_content.template.180.description') || 'Cherry trees, gentle pastels; fresh and romantic.',
      prompt:
        "Set the background in a spring blossom garden with cherry trees in full bloom, gentle pastel tones, soft natural light, and a fresh romantic atmosphere with petals. Maintain the subject’s scale and lighting direction. Preserve pose and outfit, and avoid crowds or structures.",
      category: "Dreamlike & Seasonal",
      group: "Scene",
    },
    {
      id: "181",
      name: t('social_content.template.181.name') || 'Summer Seaside Terrace',
      description: t('social_content.template.181.description') || 'Soft breeze, blue-white palette; bright and airy.',
      prompt:
        "Replace the background with a summer seaside terrace featuring ocean views, a soft breeze effect, a blue‑white palette, and a bright airy atmosphere with coastal elegance. Keep the subject’s lighting consistent and preserve pose and outfit. Ensure natural shadow grounding and avoid crowds, boats, or text signage.",
      category: "Dreamlike & Seasonal",
      group: "Scene",
    },
    {
      id: "182",
      name: t('social_content.template.182.name') || 'Moonlit Night Garden',
      description: t('social_content.template.182.description') || 'Fairy lights, cinematic shadows; enchanting atmosphere.',
      prompt:
        "Set the background to a moonlit night garden with fairy lights creating soft illumination, cinematic shadows, and an enchanting romantic atmosphere. Maintain the subject’s lighting direction and preserve pose and outfit. Avoid crowds, modern elements, or bright artificial lights.",
      category: "Dreamlike & Seasonal",
      group: "Scene",
    },
    {
      id: "183",
      name: t('social_content.template.183.name') || 'Fairy-Tale Castle Grounds',
      description: t('social_content.template.183.description') || 'Fantasy atmosphere, lush greenery; magical and whimsical.',
      prompt:
        "Place the scene on fairy‑tale castle grounds featuring a fantasy atmosphere, lush greenery, and a castle in the background with magical whimsical elements under soft natural or golden light. Keep the subject’s scale natural and preserve pose and outfit. Ensure natural integration and avoid crowds or modern elements.",
      category: "Dreamlike & Seasonal",
      group: "Scene",
    },
    // Destination & Cultural Environments
    {
      id: "184",
      name: t('social_content.template.184.name') || 'Santorini Terrace',
      description: t('social_content.template.184.description') || 'White walls, blue domes, Mediterranean sky; iconic Greek beauty.',
      prompt:
        "Set the background to a Santorini terrace with whitewashed walls, blue domes, a Mediterranean sky, and the Aegean Sea horizon. Keep the lighting consistent and preserve pose and outfit. Avoid crowds, text signage, or cruise ships.",
      category: "Destination & Cultural",
      group: "Scene",
    },
    {
      id: "186",
      name: t('social_content.template.186.name') || 'Balinese Garden Pavilion',
      description: t('social_content.template.186.description') || 'Tropical plants, soft backlight; exotic paradise.',
      prompt:
        "Set the scene in a Balinese garden pavilion surrounded by tropical plants and lush foliage with soft backlight and traditional Balinese architecture. Keep the subject’s lighting consistent and preserve pose and outfit. Ensure natural integration and avoid crowds or modern elements.",
      category: "Destination & Cultural",
      group: "Scene",
    },
    {
      id: "187",
      name: t('social_content.template.187.name') || 'Japanese Garden Bridge',
      description: t('social_content.template.187.description') || 'Minimal elegance, delicate blossoms; serene beauty.',
      prompt:
        "Replace the background with a Japanese garden bridge featuring minimal elegant design, delicate blossoms, serene water features, and traditional Japanese garden elements under soft natural light. Maintain the subject’s scale and perspective, preserve pose and outfit, and avoid crowds or modern elements.",
      category: "Destination & Cultural",
      group: "Scene",
    },
    {
      id: "188",
      name: t('social_content.template.188.name') || 'Parisian Street Café',
      description: t('social_content.template.188.description') || 'Romantic cobblestone setting; classic French charm.',
      prompt:
        "Set the background to a Parisian street café with a romantic cobblestone setting, classic French architecture, and elegant café elements under soft natural light. Keep the subject’s scale natural and preserve pose and outfit. Avoid legible text, crowds, modern signage, or vehicles.",
      category: "Destination & Cultural",
      group: "Scene",
    },
    {
      id: "189",
      name: t('social_content.template.189.name') || 'Moroccan Desert Camp',
      description: t('social_content.template.189.description') || 'Soft dunes, lantern glow; exotic desert atmosphere.',
      prompt:
        "Replace the background with a Moroccan desert camp featuring soft sand dunes, warm lantern glow, and an exotic desert atmosphere with traditional tent elements. Maintain the subject’s lighting direction and preserve pose and outfit. Ensure natural shadow grounding and avoid crowds or modern elements.",
      category: "Destination & Cultural",
      group: "Scene",
    },
  
    // === LIGHTING & STYLE ===
    {
      id: "4",
      name: t('social_content.template.4.name') || 'Golden Hour Pro',
      description: t('social_content.template.4.description') || 'Warm rim light; soft highlights; clean whites.',
      prompt:
        "Apply professional golden hour lighting with warm directional sunlight at a 45‑degree angle to create natural rim light and soft highlights on the skin. Use elongated soft shadows for depth and dimension while preserving background colors and ensuring proper exposure on white fabrics. Maintain skin tone accuracy and avoid harsh contrast.",
      category: "Lighting",
      group: "Lighting & Style",
    },
    {
      id: "32",
      name: t('social_content.template.32.name') || 'Editorial Rim Light',
      description: t('social_content.template.32.description') || 'Glow outline; subtle face fill; background preserved.',
      prompt:
        "Add a professional rim light by positioning a strong backlight behind the subject to create a glowing outline around the hair and shoulders. Use a subtle fill light to retain facial detail. Maintain skin tone accuracy and preserve the existing background.",
      category: "Lighting",
      group: "Lighting & Style",
    },
    {
      id: "34",
      name: t('social_content.template.34.name') || 'Classic Split',
      description: t('social_content.template.34.description') || 'Key at 90°; dramatic but detailed shadows.',
      prompt:
        "Apply classic split lighting by placing the key light 90 degrees to one side of the face to create a clear division between light and shadow. Use a reflector or subtle fill light to retain detail in the shadowed side, achieving a dramatic yet balanced portrait.",
      category: "Lighting",
      group: "Lighting & Style",
    },
  
    // === COMPOSITION / PHYSICS — FRAMING ===
    {
      id: "107",
      name: t('social_content.template.107.name') || 'Full-Body Frame',
      description: t('social_content.template.107.description') || 'Ideal for fashion/wedding dress showcases; includes full outfit and pose.',
      prompt:
        "Reframe the image to show the subject from head to toe. Include grounded shadows and a natural perspective to showcase the full outfit and pose. Avoid cropping hands, feet, or the veil, and preserve background continuity.",
      category: "Framing",
      group: "Composition / Physics",
    },
    {
      id: "108",
      name: t('social_content.template.108.name') || '¾ Portrait (Knees-Up)',
      description: t('social_content.template.108.description') || 'Balanced editorial framing; focus on outfit and expression.',
      prompt:
        "Reframe the image to a three‑quarter portrait (knees‑up) with balanced editorial composition. Highlight the dress silhouette and expression while maintaining lens perspective and depth of field.",
      category: "Framing",
      group: "Composition / Physics",
    },
    {
      id: "109",
      name: t('social_content.template.109.name') || 'Half-Body Portrait',
      description: t('social_content.template.109.description') || 'Emphasizes torso & gesture; used for elegant or lifestyle framing.',
      prompt:
        "Reframe the image to a half‑body portrait (mid‑torso crop) to emphasize gesture and bodice details. Keep the horizon level and avoid cropping through joints in awkward places.",
      category: "Framing",
      group: "Composition / Physics",
    },
    {
      id: "110",
      name: t('social_content.template.110.name') || 'Close-Up Portrait',
      description: t('social_content.template.110.description') || 'Head & shoulders crop; perfect for expression, makeup, jewelry.',
      prompt:
        "Reframe the image to a close‑up portrait focusing on the head and shoulders. Highlight expression, makeup, and jewelry while preserving the current lighting direction and skin realism.",
      category: "Framing",
      group: "Composition / Physics",
    },
    {
      id: "111",
      name: t('social_content.template.111.name') || 'Macro Detail Shot',
      description: t('social_content.template.111.description') || 'Close detail (fabric texture, accessories, bouquet, lace).',
      prompt:
        "Create a macro detail shot focusing on a specific element such as fabric texture, accessories, bouquet, lace patterns, beadwork, or embroidery. Use a macro lens effect with shallow depth of field to isolate the chosen detail and ensure proper lighting to reveal texture without harsh shadows or reflections.",
      category: "Framing",
      group: "Composition / Physics",
    },
  
    // === COMPOSITION / PHYSICS — CAMERA ANGLE ===
    {
      id: "112",
      name: t('social_content.template.112.name') || 'Eye-Level View',
      description: t('social_content.template.112.description') || 'Neutral, natural, flattering — the "default" look.',
      prompt:
        "Set the camera to an eye‑level view for a neutral, natural perspective with flattering proportions. Maintain the existing lighting and background blur.",
      category: "Camera Angle",
      group: "Composition / Physics",
    },
    {
      id: "113",
      name: t('social_content.template.113.name') || 'Low-Angle View',
      description: t('social_content.template.113.description') || 'Empowering, majestic, enhances gown volume.',
      prompt:
        "Set the camera to a low‑angle view (15–30° below eye level) to create an empowering, majestic perspective that enhances gown. Keep vertical lines controlled to avoid distortion.",
      category: "Camera Angle",
      group: "Composition / Physics",
    },
    {
      id: "114",
      name: t('social_content.template.114.name') || 'High-Angle View',
      description: t('social_content.template.114.description') || 'Soft, delicate, romantic perspective.',
      prompt:
        "Set the camera to a high‑angle view (15–30° above eye level) for a soft, delicate, romantic perspective. Maintain flattering facial proportions and the shape of the dress.",
      category: "Camera Angle",
      group: "Composition / Physics",
    },
    {
      id: "115",
      name: t('social_content.template.115.name') || 'Side Profile (¾ or 90°)',
      description: t('social_content.template.115.description') || 'Sculptural and elegant, classical bridal pose.',
      prompt:
        "Set the camera angle to a side profile at ¾ or 90°, whichever best fits the subject, to sculpturally and elegantly render the jawline and neckline. Maintain current lighting and facial expression.",
      category: "Camera Angle",
      group: "Composition / Physics",
    },
    {
      id: "116",
      name: t('social_content.template.116.name') || 'Over-the-Shoulder',
      description: t('social_content.template.116.description') || 'Intimate, storytelling angle; focus on gaze direction.',
      prompt:
        "Recompose the shot to an over‑the‑shoulder view with one shoulder in the foreground and the face turned toward the camera. Focus on the subject’s gaze direction while preserving current lighting, facial expression, background, and realistic depth of field. Remove the previous pose so that the person is not duplicated.",
      category: "Camera Angle",
      group: "Composition / Physics",
    },
    {
      id: "117",
      name: t('social_content.template.117.name') || 'Dynamic Tilt / Dutch Angle',
      description: t('social_content.template.117.description') || 'Fashion-forward editorial energy.',
      prompt:
        "Apply a subtle Dutch angle with a slight diagonal horizon to add fashion‑forward editorial energy. Maintain subject dignity and avoid excessive tilt that would distort dress lines.",
      category: "Camera Angle",
      group: "Composition / Physics",
    },
    {
      id: "118",
      name: t('social_content.template.118.name') || 'Walking Away View',
      description: t('social_content.template.118.description') || 'Emotional narrative, movement emphasis.',
      prompt:
        "Compose the image from behind or at a three‑quarter back angle to emphasize movement and the flow of the train or veil. Maintain an elegant posture and keep the horizon level.",
      category: "Camera Angle",
      group: "Composition / Physics",
    },
  
    // === TECHNICAL / PRECISE EDITS ===
    {
      id: "119",
      name: t('social_content.template.119.name') || 'Stray Hair Cleanup',
      description: t('social_content.template.119.description') || 'Gently removes flyaway hairs and maintains natural texture.',
      prompt:
        "Gently remove stray and flyaway hairs while preserving natural hair edge detail, sheen, volume, and texture. Keep the overall hairstyle unchanged.",
      category: "Cleanup / Retouch",
      group: "Technical",
    },
    {
      id: "120",
      name: t('social_content.template.120.name') || 'Edge Cleanup',
      description: t('social_content.template.120.description') || 'Refines outlines around veil, hair, and dress for clean cutouts.',
      prompt:
        "Refine the edges around the veil, hair, and dress to create clean cutouts. Maintain the veil’s semi‑transparency and realistic fabric edges without introducing halos.",
      category: "Cleanup / Retouch",
      group: "Technical",
    },
    {
      id: "121",
      name: t('social_content.template.121.name') || 'Detail Boost',
      description: t('social_content.template.121.description') || 'Subtle sharpening of lace, embroidery, and jewelry.',
      prompt:
        "Subtly enhance the micro‑contrast of lace, embroidery, beadwork, and jewelry with gentle sharpening. Preserve skin texture and avoid oversharpening or adding noise.",
      category: "Cleanup / Retouch",
      group: "Technical",
    },
    {
      id: "122",
      name: t('social_content.template.122.name') || 'Texture Balance',
      description: t('social_content.template.122.description') || 'Evens fabric texture, keeps realism without noise.',
      prompt:
        "Even out fabric texture and reduce hotspots while maintaining realism and avoiding noise. Reduce moiré and banding while preserving the fabric’s weave detail and neutral whites.",
      category: "Cleanup / Retouch",
      group: "Technical",
    },
    {
      id: "123",
      name: t('social_content.template.123.name') || 'Color Correction (Auto)',
      description: t('social_content.template.123.description') || 'Balances white tones and skin hues to match dress neutrality.',
      prompt:
        "Apply automatic color correction to balance white tones and skin hues so they match the dress's neutral tones. Ensure neutral, accurate rendering and preserve ambient scene colors.",
      category: "Color / Tone",
      group: "Technical",
    },
    {
      id: "192",
      name: t('social_content.template.192.name') || 'Black and White',
      description: t('social_content.template.192.description') || 'Convert the image to black and white while preserving detail and contrast.',
      prompt:
        "Convert the image to black and white with rich midtones, deep yet unclipped shadows, and clean highlights. Preserve texture, micro‑contrast, and detail. Maintain natural lighting and avoid color artifacts or sepia tones.",
      category: "Color / Tone",
      group: "Technical",
    },
    {
      id: "190",
      name: t('social_content.template.190.name') || 'Blur Background',
      description: t('social_content.template.190.description') || 'Apply a natural depth-of-field blur to the background while keeping the subject sharp.',
      prompt:
        "Blur the background with a natural depth‑of‑field effect while keeping the subject (person, dress, and accessories) completely sharp and in focus. Use a realistic bokeh effect with gradual blur that maintains natural lighting and color tones.",
      category: "Backgrounds",
      group: "Technical",
    },
    {
      id: "191",
      name: t('social_content.template.191.name') || 'Remove Background',
      description: t('social_content.template.191.description') || 'Remove the background and replace with a transparent or solid color background.',
      prompt:
        "Remove the existing background entirely and replace it with a transparent or neutral solid color. Preserve the subject (person, dress, and accessories) with clean, precise edges, maintaining hair strands, veil transparency, and fabric details without halos or missing elements.",
      category: "Backgrounds",
      group: "Technical",
    },
  
    // === CONCEPT / ARTISTIC DIRECTION ===
    {
      id: "29",
      name: t('social_content.template.29.name') || 'Luxury Editorial',
      description: t('social_content.template.29.description') || 'Refined contrast; minimalism; true colors.',
      prompt:
        "Apply a luxury editorial aesthetic through color grading. Introduce refined polish, subtle contrast shaping, and crisp details with minimalistic sophistication. Maintain realistic skin tones and true wardrobe colors. Preserve the existing composition, lighting direction, and background without heavy filters or distortions.",
      category: "Art Direction",
      group: "Concept / Artistic Direction",
    },
    {
      id: "30",
      name: t('social_content.template.30.name') || 'Rich B&W',
      description: t('social_content.template.30.description') || 'Deep but unclipped shadows; clean highlights.',
      prompt:
        "Convert the image to black and white with rich midtones, deep yet unclipped shadows, and clean highlights. Preserve texture and micro‑contrast and avoid color artifacts or sepia tones.",
      category: "Art Direction",
      group: "Concept / Artistic Direction",
    },
  ];